if (!customElements.get('image-tiles')) {
  const debounceFrame = (fn) => {
    let frame;
    return () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fn);
    };
  };

  /**
   * Expandable image tiles. The "+" on a tile widens it to the right while
   * the other tiles keep their width, so on desktop the row overflows and
   * scrolls like the product carousels. Once the width has settled the tile
   * reveals its description and side panel. One tile is open at a time.
   *
   * The two steps are separate classes so the extra content never lays out
   * inside a tile that is still narrow:
   *   is-expanded — width (CSS transition on flex-basis)
   *   is-open     — content, added after the width transition
   */
  class ImageTiles extends HTMLElement {
    // Overwritten by initDrag() for mouse users.
    updateGrabbable() {}

    connectedCallback() {
      this.tiles = [...this.querySelectorAll('[data-tile-expandable]')];
      if (!this.tiles.length) return;

      this.scroller = this.querySelector('[data-tiles-scroller]');
      this.items = [...this.scroller.children];
      this.columns = Number(this.dataset.columns) || 3;
      this.expandRatio = Number(this.dataset.expandRatio) || 1;
      this.duration = Number(this.dataset.duration) || 0;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.duration = 0;
      this.desktopQuery = window.matchMedia('(min-width: 769px)');

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      if (window.matchMedia('(pointer: fine)').matches) this.initDrag();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      cancelAnimationFrame(this.scrollFrame);
    }

    onClick(evt) {
      const toggle = evt.target.closest('[data-tile-toggle]');
      if (!toggle) return;

      evt.preventDefault();
      const tile = toggle.closest('[data-tile-expandable]');
      if (tile.classList.contains('is-expanded')) {
        this.close(tile);
      } else {
        this.open(tile);
      }
    }

    onKeydown(evt) {
      if (evt.key !== 'Escape') return;
      const tile = this.querySelector('[data-tile-expandable].is-expanded');
      if (!tile) return;

      this.close(tile);
      tile.querySelector('[data-tile-toggle]').focus();
    }

    // Open the tile being edited in the theme editor so its panel is visible.
    onBlockSelect(evt) {
      const tile = this.tiles.find((el) => el.contains(evt.target));
      if (tile && !tile.classList.contains('is-expanded')) this.open(tile);
    }

    open(tile) {
      this.tiles.forEach((el) => {
        if (el !== tile && el.classList.contains('is-expanded')) this.close(el);
      });

      clearTimeout(tile.expandTimer);
      tile.classList.add('is-expanded');
      this.setToggleState(tile, true);
      this.scrollIntoRow(tile);
      tile.expandTimer = setTimeout(() => {
        tile.classList.add('is-open');
        this.updateGrabbable();
      }, this.duration);
    }

    close(tile) {
      clearTimeout(tile.expandTimer);
      tile.classList.remove('is-open', 'is-expanded');
      this.setToggleState(tile, false);
      tile.expandTimer = setTimeout(() => this.updateGrabbable(), this.duration);
    }

    setToggleState(tile, expanded) {
      const toggle = tile.querySelector('[data-tile-toggle]');
      toggle.setAttribute('aria-expanded', expanded);
      toggle.setAttribute('aria-label', expanded ? toggle.dataset.labelClose : toggle.dataset.labelOpen);
    }

    /**
     * Scrolls the row, alongside the width transition and on the same curve,
     * so the opening tile ends fully in view. Its end position is worked out
     * up front from the closed tile width, since every other tile is closed
     * by then.
     * @param {Element} tile - The tile being opened.
     */
    scrollIntoRow(tile) {
      if (!this.desktopQuery.matches) return;

      const viewWidth = this.scroller.clientWidth;
      const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
      const tileWidth = (viewWidth - (this.columns - 1) * gap) / this.columns;
      const index = this.items.indexOf(tile);
      const left = index * (tileWidth + gap);
      const right = left + tileWidth * this.expandRatio;

      const from = this.scroller.scrollLeft;
      const to = Math.max(0, Math.min(left, Math.max(from, right - viewWidth)));
      if (Math.abs(to - from) < 1) return;

      cancelAnimationFrame(this.scrollFrame);
      if (!this.duration) {
        this.scroller.scrollLeft = to;
        return;
      }

      const start = performance.now();
      const step = (now) => {
        const progress = Math.min(1, (now - start) / this.duration);
        this.scroller.scrollLeft = from + (to - from) * ImageTiles.ease(progress);
        if (progress < 1) this.scrollFrame = requestAnimationFrame(step);
      };
      this.scrollFrame = requestAnimationFrame(step);
    }

    /**
     * Ease-in-out cubic, the same curve as the CSS cubic-bezier(0.65, 0, 0.35, 1).
     * @param {number} t - Progress, 0 to 1.
     * @returns {number}
     */
    static ease(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
    }

    /**
     * Click-and-drag scrolling for mouse users, as on the theme's carousels.
     * A drag swallows the click that ends it, so it doesn't follow a link.
     */
    initDrag() {
      const { scroller } = this;
      let startX = 0;
      let startScroll = 0;
      let down = false;
      let dragged = false;

      this.updateGrabbable = () => {
        scroller.classList.toggle('is-grabbable', scroller.scrollWidth > scroller.clientWidth + 1);
      };
      this.updateGrabbable();
      window.addEventListener('resize', debounceFrame(this.updateGrabbable));

      scroller.addEventListener('mousedown', (evt) => {
        if (evt.button !== 0 || evt.target.closest('button')) return;
        down = true;
        dragged = false;
        startX = evt.pageX;
        startScroll = scroller.scrollLeft;
      });

      scroller.addEventListener('mousemove', (evt) => {
        if (!down) return;
        const dx = evt.pageX - startX;
        if (!dragged && Math.abs(dx) < 5) return;

        dragged = true;
        cancelAnimationFrame(this.scrollFrame);
        scroller.classList.add('is-grabbing');
        scroller.scrollLeft = startScroll - dx;
        evt.preventDefault();
      });

      const end = () => {
        down = false;
        scroller.classList.remove('is-grabbing');
      };
      scroller.addEventListener('mouseup', end);
      scroller.addEventListener('mouseleave', end);

      scroller.addEventListener('click', (evt) => {
        if (!dragged) return;
        dragged = false;
        evt.preventDefault();
        evt.stopPropagation();
      }, true);

      scroller.addEventListener('dragstart', (evt) => evt.preventDefault());
    }
  }

  customElements.define('image-tiles', ImageTiles);
}
